import { format, parseISO, startOfMonth } from 'date-fns'

import {
  isClosingDateBasis,
  SEARCH_DATE_BASIS,
  type SearchDateBasis
} from '@/features/shared/models'
import {
  type ChargeStatusType,
  type ContractTypeWithError
} from '@/libs/api/types'
import { filterObjectEmpty, safeFormatISO } from '@/libs/utils'

import { isOperatingDateRangeWithinMaxMonths } from './operating-date-range'
import { type ToursSearch } from './tours-search'

/**
 * 運行実績検索フォームの値
 */
export type ToursSearchFormValue = {
  page: number
  /** 運行実績ステータス */
  status: ChargeStatusType | null
  /** 日報番号 */
  dailyReportNo: string
  /** 運行日 From */
  operatingDateFrom: Date | null
  /** 運行日 To */
  operatingDateTo: Date | null
  /** 検索日付基準（通常 / 締日基準・請求月 / 締日基準・支払月）。VOC_PJ-939 #4387 */
  searchDateBasis: SearchDateBasis
  /** 締日基準モード時の対象年月。DEFAULT 時は送信しない */
  operatingYearMonth: Date | null
  /** 契約名 */
  contractName: string
  /** 契約種別 */
  contractType: ContractTypeWithError | null
  /** 荷主ID */
  customerIds: number[] | null
  /** パートナーID */
  partnerIds: number[] | null
  /** 車両番号 */
  truckCode: string
  /** 修正の有無  */
  hasUpdated: boolean | null
  /** 超過時間割増の有無  */
  hasOvertime: boolean | null
  /** 深夜時間割増の有無  */
  hasNightTime: boolean | null
  /** 超過距離割増の有無  */
  hasExcessDistance: boolean | null
  /** 追加業務の有無  */
  hasAddSite: boolean | null
  /** 手動追加フラグ（true: 手動追加） */
  isManualTour: boolean | null
  /** ドライバーID */
  driverId: number | null
  /** キャンセル運行を表示するかどうか */
  showCancelledTours: boolean
  /** カスタムキー横断フリーワード検索（請求明細の customKey1〜5 のいずれかに部分一致。VOC_PJ-960 #4487） */
  customKeyFreeWord: string
}

/**
 * 運行実績検索フォームの初期値
 */
export const defaultToursSearchFormValue: ToursSearchFormValue = {
  page: 1,
  status: null,
  dailyReportNo: '',
  operatingDateFrom: null,
  operatingDateTo: null,
  searchDateBasis: SEARCH_DATE_BASIS.DEFAULT,
  operatingYearMonth: null,
  contractName: '',
  contractType: null,
  customerIds: null,
  partnerIds: null,
  truckCode: '',
  hasUpdated: null,
  hasOvertime: null,
  hasNightTime: null,
  hasExcessDistance: null,
  hasAddSite: null,
  isManualTour: null,
  driverId: null,
  showCancelledTours: false,
  customKeyFreeWord: ''
}

/**
 * 運行日バリデーション結果
 */
export type OperatingDateValidationResult =
  | { isValid: true }
  | { isValid: false; errorType: 'required' | 'exceeded' }

/**
 * 運行日のバリデーションを行う（#4557）
 *
 * 検索条件の多くが LIKE '%…%' で索引が効かないため、母集合を運行日で必ず絞る。
 * 配送依頼一覧の発送日（`validateShippingDateRange`）と同じルールに揃えている。
 *
 * 締日基準検索（#4387）では計上月が母集合を絞るため、`isRequired: false` を渡して
 * 必須チェックだけを外す（Q-8 = 8-A）。BE 側の shouldValidateOperatingDate と同じ規則で、
 * 片側だけの指定はレンジとして不完全なので required、両方指定なら上限を効かせる。
 *
 * @param operatingDateFrom 運行日（開始日）
 * @param operatingDateTo 運行日（終了日）
 * @param options.isRequired From/To の指定を必須とするか（既定 true）
 * @returns バリデーション結果
 */
export const validateOperatingDateRange = (
  operatingDateFrom: Date | null,
  operatingDateTo: Date | null,
  options?: { isRequired?: boolean }
): OperatingDateValidationResult => {
  const isRequired = options?.isRequired ?? true

  // 必須が外れているときに限り、両方未指定を「レンジ指定なし」として通す
  if (!isRequired && !operatingDateFrom && !operatingDateTo) {
    return { isValid: true }
  }

  // 必須チェック
  if (!operatingDateFrom || !operatingDateTo) {
    return { isValid: false, errorType: 'required' }
  }

  // 期間上限チェック
  if (
    !isOperatingDateRangeWithinMaxMonths(operatingDateFrom, operatingDateTo)
  ) {
    return { isValid: false, errorType: 'exceeded' }
  }

  return { isValid: true }
}

/**
 * 運行実績の検索条件から検索フォームの値に変換する
 */
export const mapToToursSearchFormValue = ({
  operatingDateFrom,
  operatingDateTo,
  searchDateBasis,
  operatingYearMonth,
  ...rest
}: ToursSearch): ToursSearchFormValue => {
  return {
    ...defaultToursSearchFormValue,
    ...rest,
    operatingDateFrom: operatingDateFrom ? parseISO(operatingDateFrom) : null,
    operatingDateTo: operatingDateTo ? parseISO(operatingDateTo) : null,
    searchDateBasis: searchDateBasis ?? SEARCH_DATE_BASIS.DEFAULT,
    operatingYearMonth: operatingYearMonth ? parseISO(operatingYearMonth) : null
  }
}

/**
 * 運行実績検索フォームの値を検索条件に変換する。
 *
 * 締日基準モード（請求月/支払月）では検索基準と計上月を加えて送る。
 * 運行日レンジは併用でき、指定されていれば AND で絞り込まれる
 * （Q-8 = 8-A、改訂 (10)）。
 */
export const mapToToursSearch = (
  {
    operatingDateFrom,
    operatingDateTo,
    searchDateBasis,
    operatingYearMonth,
    ...rest
  }: ToursSearchFormValue,
  preservedTake?: number
): ToursSearch => {
  const filteredRest = filterObjectEmpty({
    ...rest,
    page: 1,
    take: preservedTake
  })

  const dateRange = {
    operatingDateFrom: operatingDateFrom
      ? safeFormatISO(operatingDateFrom)
      : '',
    operatingDateTo: operatingDateTo ? safeFormatISO(operatingDateTo) : ''
  }

  if (isClosingDateBasis(searchDateBasis)) {
    return {
      ...filteredRest,
      ...dateRange,
      searchDateBasis,
      operatingYearMonth: operatingYearMonth
        ? format(startOfMonth(operatingYearMonth), 'yyyy-MM-dd')
        : ''
    }
  }

  return {
    ...filteredRest,
    ...dateRange
  }
}
